import { bool, func, node, string } from 'prop-types'
import { useEffect, useRef, useState } from "react"
import Tip from "../Tip";
import { useBodyScrollLock, useModalEscape, useModalFocusTrap } from '@laramie/ui';

const Modal = ({ show, size, title, header, body, footer, footerClassName, allowFullscreen, tipName, className, onEscape, dismissOnEscape, focusPrimary }) => {

    const [maximized, setMaximized] = useState(false)
    const [finalSize, setFinalSize] = useState(size)
    const modalRef = useRef(null)

    useModalEscape(() => onEscape?.(), show && dismissOnEscape && !!onEscape, () => modalRef.current)
    useBodyScrollLock(show)
    useModalFocusTrap(show, () => modalRef.current)

    useEffect(() => {
		setFinalSize('modal-' + (maximized ? 'fullscreen' : size))
	}, [maximized,size])

    useEffect(() => {
        if (show && modalRef.current) {
            if (focusPrimary) {
                const primaryButton = modalRef.current.querySelector('.modal-footer .btn-primary:not([disabled])')
                if (primaryButton) {
                    primaryButton.focus()
                    return
                }
            }
            const focusableElements = modalRef.current.querySelectorAll(
                'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
            );
            if (focusableElements.length > 0) {
                focusableElements[0].focus();
            }
        }
    }, [show, focusPrimary]);

    useEffect(() => {
        if (!show || !focusPrimary || !modalRef.current) return
        const modalNode = modalRef.current
        const handleKeyDown = (event) => {
            if (event.key === 'Enter' && event.repeat) event.preventDefault()
        }
        modalNode.addEventListener('keydown', handleKeyDown, true)
        return () => modalNode.removeEventListener('keydown', handleKeyDown, true)
    }, [show, focusPrimary])

    return !show ? <></> : (
        <div className={`modal modal-block ${className || ''}`} role="dialog" data-keyboard="false" data-backdrop="static" ref={modalRef}>
            <div className={`modal-dialog ${finalSize}`}>
                <div className="modal-content animated fadeIn">
                    <div className="modal-header">
                        {!!title && <h2 className="modal-title">{title}</h2>}
                        {header}
                        <div className="d-flex align-items-center gap-1">
                            {tipName && <Tip name={tipName} position="bottom" compact />}
                            {allowFullscreen && (
                                <div onClick={() => setMaximized(!maximized)} className="link float-end m-left-5">
                                    {(maximized) ? (
                                        <span className="material-symbols-outlined" title="Minimizar editor de fórmula">zoom_in_map</span>
                                    ) : (
                                        <span className="material-symbols-outlined" title="Maximizar editor de fórmula">zoom_out_map</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="modal-body">
                        {body}
                    </div>
                    <div className={`modal-footer ${footerClassName || ''}`.trim()}>
                        {footer}
                    </div>
                </div>
            </div>
        </div>
    )
}

Modal.propTypes = {
    show: bool,
    size: string,
    allowFullscreen: bool,
    title: string,
    header: node,
    body: node,
    footer: node,
    tipName: string,
    className: string,
    onEscape: func,
    dismissOnEscape: bool,
    footerClassName: string,
    focusPrimary: bool
}

Modal.defaultProps = {
    show: true,
    size: 'lg',
    allowFullscreen: false,
    title: "",
    header: null,
    body: null,
    footer: null,
    tipName: "",
    className: '',
    onEscape: null,
    dismissOnEscape: true,
    footerClassName: '',
    focusPrimary: false
}

export default Modal
